// The string catalogue: every word the surface supplies itself. Labels, tags, button text, accessible
// names, empty and error lines. Text that arrives from the session — sentences, nudge text, step
// titles, card content, persona lines — is content, not chrome: it never goes here and is never
// altered by the surface.
//
// Wording follows mockups/CONTRACT.md §3 and the kit's components: sentence case, plain words, no
// exclamation marks, no emoji. Several entries are fixed wording other E09 tasks test for; change
// one only together with the task that relies on it.
//
// A value is either a template or a set of plural forms chosen by Intl.PluralRules from `count`.
// `{name}` marks a parameter; t() fills it and throws when it is missing.
export const en = {
    // ── Identity and disclosure ───────────────────────────────────────────
    "identity.ai": "AI assistant",
    "identity.aiBadge": "AI",
    "identity.notAPerson": "An AI assistant, not a person",
    "disclosure.title": "About this assistant",
    "disclosure.body": "This is an AI assistant. It sees only the screens your firm has approved, and nothing else. It explains and points. You make every decision and every commit.",
    "disclosure.dismiss": "Got it",
    // ── The orb's accessible name, one per state ──────────────────────────
    "orb.idle": "AI assistant, ready",
    "orb.looking": "AI assistant, looking at the screen",
    "orb.thinking": "AI assistant, checking the guide",
    "orb.speaking": "AI assistant, speaking",
    "orb.alert": "AI assistant, with a warning for you",
    "orb.paused": "AI assistant, paused and not looking",
    // The orb posture's unread dot, as the kit's collapsed Companion says it (mockups/companion.js).
    "orb.unread": "There is something new",
    // ── Header: Seeing chip, eye, tier, menu ──────────────────────────────
    "header.seeing": "Seeing: {title}",
    "header.seeingUnknown": "No approved procedures for this screen",
    "header.waiting": "Waiting for a screen",
    "header.notLookingInWindow": "Not looking in this window",
    "header.eyeLooking": "Looking at approved screens",
    "header.eyePaused": "Not looking",
    "header.pause": "Pause looking",
    "header.resume": "Start looking again",
    "header.menu": "Settings",
    "header.quietFlag": "Quiet mode: only warnings come through",
    "menu.seen": "What I can see",
    "menu.record": "Session record",
    "menu.training": "Training",
    "menu.autonomy": "What I may do",
    "menu.shortcuts": "Keyboard shortcuts",
    "menu.guidance": "Guidance level",
    "menu.disclosure": "About this assistant",
    "menu.speak": "Speak replies",
    "menu.captionsOnly": "Captions only in this browser",
    "menu.reset": "Reset session",
    "menu.resetConfirm": "Click again to clear the session",
    // New chrome copy (E13-S5-T4): the hands-free setting.
    "menu.handsFree": "Hands-free",
    "menu.handsFreeHint": "Listens without holding the button. Turns itself off after a quiet spell.",
    // ── Perception tiers, in words (docs/03-architecture.md, perception tiers) ──
    "tier.0": "Reading which screen is open, nothing on it",
    "tier.1": "Reading approved fields, counts and open windows",
    "tier.values": "Reading approved fields and ranges; identifiers stay masked",
    "tier.valuesTokens": "Reading approved fields, ranges and tokens",
    // ── Guidance level ────────────────────────────────────────────────────
    "guidance.full": "Full",
    "guidance.light": "Light",
    "guidance.quiet": "Quiet",
    "guidance.automatic": "Automatic",
    "guidance.quietHint": "Warnings still come through",
    // ── Postures ──────────────────────────────────────────────────────────
    "posture.label": "Posture",
    "posture.orb": "Orb",
    "posture.orbHint": "Folded down to the orb",
    "posture.companion": "Companion",
    "posture.companionHint": "Docked beside the platform",
    "posture.container": "Container",
    "posture.containerHint": "Hosts the platform inside its layout",
    "posture.hud": "HUD",
    "posture.hudHint": "Floats over the platform",
    "posture.practice": "Practice",
    "posture.toOrb": "Minimise to the orb",
    "posture.fromOrb": "Open {persona}",
    "posture.fromOrbUnnamed": "Open the AI assistant",
    "posture.fromOrbAi": "Open {persona}, the AI assistant",
    // ── Steps and decisions ───────────────────────────────────────────────
    "step.tagVerified": "Verified on screen",
    "step.tagPassed": "Seen further on",
    "step.tagAcknowledged": "You confirmed",
    "decision.tagInferred": "Read from the screen",
    "decision.tagAsked": "You chose",
    "step.chip": "Step {ordinal} · {title}",
    "step.chipOrdinal": "Step {ordinal}",
    "step.chipOf": "Step {ordinal} of {total} · {title}",
    "step.count": "Step {ordinal} of {total}",
    "step.markDone": "Mark as done",
    "step.takeMeThere": "Take me there",
    "step.firmRule": "Firm rule",
    "step.watchOut": "Watch out",
    "step.watchFor": "Watch for",
    "step.dependsOnDecision": {
        one: "The last step depends on your decision",
        other: "The last {count} steps depend on your decision",
    },
    "walkthrough.start": "Work a case with me",
    "walkthrough.again": "Work another case with me",
    "walkthrough.complete": "Walkthrough complete",
    "walkthrough.stop": "Stop",
    "walkthrough.progress": "Walkthrough progress",
    // The stepper (E09-S4-T1): the procedure's id and version under its title, and the outcome.
    "stepper.sub": "{id} · version {version}",
    "outcome.escalation": "Goes to a person",
    "outcome.code": "Disposition code",
    // ── Now card ──────────────────────────────────────────────────────────
    "now.seeing": "Seeing",
    "now.paused": "Paused",
    "now.notLooking": "Not looking",
    "now.nothingInView": "Nothing in view yet. I pick up as soon as an approved screen opens.",
    "now.unknownScreen": "Nothing is approved for this screen, so I stay quiet here.",
    // New chrome copy (E09-S4-T3): the kit's waiting line under a step the screen will show done
    // (stepBody() in mockups/kit/components/guide.js).
    "now.willTick": "I'll tick this when I see it on screen.",
    "now.openLoops": {
        one: "{count} thing is still yours to finish",
        other: "{count} things are still yours to finish",
    },
    // ── Nudges ────────────────────────────────────────────────────────────
    "nudge.info": "Note",
    "nudge.caution": "Caution",
    "nudge.stop": "Stop",
    "nudge.guardrail": "Guardrail",
    "nudge.showMe": "Show me",
    "nudge.gotIt": "Got it",
    "nudge.acknowledged": "Acknowledged",
    "nudge.noLongerApplies": "No longer applies",
    "nudge.addedToLoops": "Added to your open loops",
    "nudge.none": "No warnings waiting.",
    "nudge.dismiss": "Dismiss",
    // A stop nudge's one control, and what its card says between the press and the state that drops it
    // (E09-S4-T4). The count is of the nudges past the list's max.
    "nudge.understand": "I understand",
    "nudge.pending": "Recording your acknowledgement",
    "nudge.more": "{count} more waiting",
    // ── Announcements (E09-S8-T3) ─────────────────────────────────────────
    // What the Announcer says for a nudge and for a change of step. {severity} is nudge.stop,
    // nudge.caution or nudge.info; {short} and {title} are the nudge's and the step's own text, as given.
    "announce.nudge": "{severity}: {short}",
    "announce.step": "Step {ordinal}: {title}",
    "announce.stepOf": "Step {ordinal} of {total}: {title}",
    // ── Open loops ────────────────────────────────────────────────────────
    "loops.title": "Open loops",
    "loops.subtitle": "Things that are still yours to finish.",
    "loops.done": "Done",
    "loops.empty": "Nothing left open.",
    // The check control's tooltip and accessible names, from openLoops() in mockups/kit/components/
    // trust.js; {text} is the loop's own text, verbatim. The pending line is new (E09-S4-T5): the kit
    // ticks a loop the moment it is pressed, and here it is done only when state says so.
    "loops.markDone": "Mark as done",
    "loops.markDoneOf": "Mark as done: {text}",
    "loops.doneOf": "Done: {text}",
    "loops.pending": "Recording it as done",
    // ── Note draft ────────────────────────────────────────────────────────
    "note.title": "Note draft",
    "note.blank": "yours to fill in",
    // The line under a draft that still has a blank. The one-blank form is the kit's (noteCard() in
    // mockups/kit/components/cards.js); the plural is new chrome copy (E09-S4-T6).
    "note.blankLegend": {
        one: "The marked blank is yours to fill in. I will not choose a reason for you.",
        other: "The {count} marked blanks are yours to fill in. I will not choose them for you.",
    },
    "note.draft": "Draft the note",
    "note.redraft": "Redraft the note",
    "note.copy": "Copy",
    "note.copied": "Copied",
    "note.replaced": "Replaced",
    "note.counter": "{used} of {max} characters",
    // New chrome copy (E09-S4-T6): a blank's label, with the card's own hint; why Copy is withheld; and
    // a copy the browser refused. The surface never pastes, so each line leaves the next move to you.
    // A blank's label: the card's hint, then note.blank ("yours to fill in"), in this order.
    "note.blankOf": "{hint}: {blank}",
    "note.tooLong": {
        one: "This draft is {count} character over the field's {max}, so it cannot be copied.",
        other: "This draft is {count} characters over the field's {max}, so it cannot be copied.",
    },
    "note.hasToken": {
        one: "This draft holds {tokens}, a stand-in for an identifier. In the firm's audit trail it would mean nothing to anyone else, so it cannot be copied.",
        other: "This draft holds {tokens}, stand-ins for identifiers. In the firm's audit trail they would mean nothing to anyone else, so it cannot be copied.",
    },
    "note.copyFailed": "The note could not be copied. Select the text and copy it yourself.",
    // ── Point to ask and the field card ───────────────────────────────────
    "point.start": "Point at a field to ask about it",
    "point.stop": "Stop pointing",
    "point.prompt": "Click a field on the screen and I'll explain it.",
    "point.cancel": "Cancel",
    "point.notApproved": "Nothing approved can be read there.",
    "field.label": "Field",
    "field.pointedAt": "Pointed at {label}",
    "field.reachRead": "Passed through as shown.",
    "field.reachBanded": "Banded: I receive a range, never the number.",
    "field.reachPresence": "Presence only: I know whether it is there, never the number or a range.",
    "field.reachTokenised": "Tokenised: I receive a stand-in, never the real identifier.",
    "field.reachMasked": "Masked before it leaves your browser. I only know whether it is there.",
    "field.reachMaskedLength": "Masked before it leaves your browser. I know it is there and its length, never the text.",
    "field.reachMaskedCard": "Masked before it leaves your browser: its content never reaches me.",
    // The field card (E09-S3-T4): the plain gloss's overline, the card's source line, the badge and
    // line for a field the server says it did not read, and the lead-in to what this view's latest
    // frame carried for the field when the card says nothing of its treatment.
    "field.plain": "In plain words",
    "field.source": "From the guide's field table",
    "field.treatNotRead": "Not read",
    "field.reachNotRead": "Not read: nothing from this field reaches me.",
    "field.fromScreen": "From this screen:",
    // ── The escalation card (E09-S3-T4) ───────────────────────────────────
    // The overline is the kit's (escalationCard() in mockups/kit/components/cards.js). The note box
    // under it is new: the kit has none.
    "escalation.title": "Goes to a person",
    "escalation.noteLabel": "Add a note to go with this",
    "escalation.send": "Send note",
    "escalation.sent": "Note sent",
    // ── What I can see ────────────────────────────────────────────────────
    "seen.title": "What I can see",
    "seen.subtitle": "The latest screen context frame, exactly as it reached me.",
    "seen.nothing": "Nothing is being read in this view.",
    "seen.paused": "Paused. Nothing new reaches me.",
    "seen.screen": "Screen",
    "seen.matchedBy": "Matched by",
    "seen.confidence": "Confidence",
    "seen.tier": "Tier",
    "seen.source": "Source",
    "seen.overlays": "Open windows",
    "seen.entities": "Entities",
    "seen.collections": "Lists",
    "seen.signals": "Signals",
    "seen.none": "None",
    "seen.field": "Field",
    "seen.asReceived": "As received",
    "seen.noFields": "No approved fields on this screen.",
    "seen.treatRead": "Read",
    "seen.treatBanded": "Banded",
    "seen.treatPresence": "Presence",
    "seen.treatTokenised": "Tokenised",
    "seen.treatMasked": "Masked",
    "seen.present": "Present",
    "seen.notPresent": "Not present",
    "seen.maskedLength": {
        one: "Masked, {count} character",
        other: "Masked, {count} characters",
    },
    // The sentences on what was sent (after the kit's redactionSentence): each field counted by how it
    // left the device, from the same treatments the rows show; each list by its counts alone; and no
    // count of what was not read: the last sentence says no other field was (D-102). Overlays, focus and
    // signals are ids, not fields, and the panel lists them. The parts list holds only kinds that occur.
    "seen.sent": {
        one: "Sent {count} approved field: {parts}.",
        other: "Sent {count} approved fields: {parts}.",
    },
    "seen.sentNone": "No field was sent.",
    "seen.sentLists": {
        one: "Of {count} list, only its counts.",
        other: "Of {count} lists, only their counts.",
    },
    "seen.sentRest": "No other field on this screen was read.",
    "seen.sentRead": "{count} as shown",
    "seen.sentBanded": "{count} as a range",
    "seen.sentPresence": "{count} as present or absent only",
    "seen.sentTokenised": "{count} as a stand-in",
    "seen.sentMasked": "{count} masked",
    "seen.sentRaw": "{count} as sent",
    "seen.rawFrame": "Raw frame",
    // New chrome copy (E09-S6-T1). 'How', 'Cursor in' and the line under a list are the kit's
    // (mockups/kit/components/trust.js); the rest name parts of the frame the kit shows differently.
    "seen.how": "How",
    "seen.context": "Context",
    "seen.focus": "Cursor in",
    "seen.degraded": "Degraded: {reason}",
    "seen.blank": "Blank",
    "seen.treatRaw": "As sent",
    "seen.rowCount": {
        one: "{count} row on this page",
        other: "{count} rows on this page",
    },
    "seen.selectedCount": "{count} selected",
    "seen.pageOf": "page {page} of {pages}",
    "seen.page": "page {page}",
    "seen.pages": {
        one: "{count} page",
        other: "{count} pages",
    },
    "seen.pageSize": "{count} per page",
    "seen.total": "{count} in all",
    "seen.countsOnly": "Row values are not read. Only the counts above reach me.",
    // ── Composer and conversation ─────────────────────────────────────────
    "composer.placeholder": "Ask about this screen",
    "composer.ask": "Ask {persona}",
    "composer.send": "Send",
    "composer.holdToTalk": "Hold to talk",
    "composer.listening": "Listening",
    // New chrome copy (E13-S5-T3): the microphone while the browser has not handed it over, and the
    // line when an utterance stops itself at the one-minute limit.
    "composer.waitingForMic": "Waiting for the microphone",
    "composer.timeLimit": "Stopped listening after a minute. Hold to talk again.",
    // New chrome copy (E13-S5-T4): hands-free's line while the microphone is open, its control to
    // turn it off, the line when it turned itself off (the window was hidden, or five minutes of
    // quiet) or could not start, and the time-limit line while hands-free is on.
    "composer.handsFree": "Hands-free",
    "composer.handsFreeOff": "Turn off hands-free",
    "composer.handsFreeStopped": "Hands-free turned itself off.",
    "composer.handsFreeFailed": "Hands-free could not start listening. Turn it on again.",
    "composer.timeLimitHandsFree": "Stopped listening after a minute. Pause, then speak again.",
    "composer.speak": "Speak replies",
    "composer.stopSpeaking": "Stop speaking replies",
    "composer.demoVoice": "Demo voice input",
    "composer.offline": "Offline. What you send now goes when the connection is back.",
    "conversation.label": "Conversation with {persona}",
    "conversation.thinking": "Checking the guide",
    "conversation.waiting": "Waiting for a screen I recognise.",
    "conversation.notLooking": "Not looking at the moment.",
    "conversation.labelUnnamed": "Conversation with the AI assistant",
    "conversation.stillWriting": "Still writing",
    "conversation.newMessages": "New messages",
    "conversation.refusal": "Guardrail",
    // ── Citations under a message (E09-S3-T3) ─────────────────────────────
    // The chip shows the pages as the citation gives them; its name is the whole source.
    "cite.guide": "Guide",
    "cite.name": "Source: {doc}, {pages}",
    "cite.nameSection": "Source: {doc}, {section}, {pages}",
    "cite.title": "Source",
    "cite.document": "Document",
    "cite.pages": "Pages",
    "cite.section": "Section",
    "cite.version": "Version",
    // ── Clips and training ────────────────────────────────────────────────
    "clip.unavailable": "Clip not available in this copy",
    "clip.stageEmpty": "Clips from the training videos play here.",
    "clip.showMe": "Show me",
    "clip.showMeSeconds": "Show me ({seconds} s)",
    "clip.play": "Play",
    "clip.pause": "Pause",
    "clip.replay": "Play again",
    "clip.close": "Close the clip",
    "clip.progress": "Clip progress",
    "clip.from": "{id} · {title}",
    "clip.seconds": "{seconds} s",
    "clip.time": "{at} / {length}",
    "clip.captions": "Captions",
    // The video intro card the player draws before a title (ADR-036).
    "intro.region": "Video introduction",
    "intro.guide": "Guide",
    "intro.section": "Section",
    "intro.topic": "Topic",
    "intro.skip": "Skip intro",
    "intro.version": "Version {number}",
    "intro.versionPublished": "Version {number} · Published {date}",
    "intro.edition": "Guide edition {edition}",
    "training.title": "Training",
    "training.notRecorded": "Not yet recorded",
    "training.required": "Required",
    "training.watched": "Watched",
    "training.notWatched": "Not watched yet",
    "training.forThisScreen": "For this screen",
    "training.runtime": {
        one: "{count} minute",
        other: "{count} minutes",
    },
    // ── Curriculum (E09-S5-T4) and the completion badge (E09-S5-T3) ───────
    // Tier names are the contract's TitleTier values; the surface supplies the words.
    "curriculum.tier.Foundation": "Foundation",
    "curriculum.tier.Core": "Core",
    "curriculum.tier.Efficiency": "Efficiency",
    "curriculum.tier.Situational": "Situational",
    "curriculum.tier.Oversight": "Oversight",
    "curriculum.count": "{done} of {total}",
    "curriculum.countLabel": "{done} of {total} recorded titles completed",
    "curriculum.play": "Play {title}, {runtime}",
    "curriculum.planned": "{runtime} planned",
    "completion.notStarted": "Not started",
    "completion.inProgress": "{pct}%",
    "completion.inProgressLabel": "In progress, {pct}% watched",
    "completion.completed": "Completed",
    "completion.attestationNeeded": "Not attested",
    "completion.attestationNeededLabel": "Watched, not yet attested",
    "completion.attestationOutOfDate": "Out of date",
    "completion.attestationOutOfDateLabel": "Attestation out of date",
    // ── Supervisory record ────────────────────────────────────────────────
    "record.title": "Session record",
    "record.subtitle": "What happened, when, and how I knew.",
    "record.export": "Export record",
    "record.exportFailed": "The record could not be exported. The timeline below is unchanged.",
    "record.empty": "Nothing recorded yet.",
    "record.verifiedLegend": "reported by this device",
    // New chrome copy (E09-S6-T4): the export button's label at the compact density, as the kit has it.
    "record.exportShort": "Export",
    // ── Autonomy ladder ───────────────────────────────────────────────────
    "autonomy.title": "What I may do",
    // The ladder's subtitle (E09-S6-T3), the kit's COPY.youDecide (mockups/kit/content.js).
    "autonomy.subtitle": "You decide. You commit.",
    "autonomy.observe": "Observe",
    "autonomy.observeNote": "Read approved fields on approved screens",
    "autonomy.explain": "Explain",
    "autonomy.explainNote": "Answer from the guide, with the page",
    "autonomy.point": "Point",
    "autonomy.pointNote": "Highlight the control a step refers to",
    "autonomy.navigate": "Navigate",
    "autonomy.navigateNote": "Open a screen when you ask",
    "autonomy.prepare": "Prepare",
    "autonomy.prepareNote": "Draft a note for you to paste",
    "autonomy.commit": "Commit",
    "autonomy.commitNote": "Always you. Never the assistant.",
    "autonomy.alwaysYou": "Always you",
    "autonomy.allowed": "Allowed",
    "autonomy.off": "Off for this organisation",
    // ── Shared controls and errors ────────────────────────────────────────
    "common.close": "Close",
    "common.cancel": "Cancel",
    "common.done": "Done",
    "common.characterCount": "{used} of {max} characters",
    "error.unavailable": "The assistant can't answer right now. Steps and warnings still work.",
    "error.reconnecting": "Connection lost. Trying again.",
};
//# sourceMappingURL=en.js.map